import type { Map as MapLibreMap } from 'maplibre-gl';

/**
 * A map marker drawn from an SVG source.
 *
 * `width` / `height` are the CSS-pixel size of the whole image, shadow bleed
 * included. `glyphHeight` is the height of the drawn shape alone and
 * `bottomPadding` the transparent band under it: a bottom-anchored pin must be
 * pushed down by that much (`icon-offset`) for its tip to land on the coordinate.
 */
export interface MapSprite {
  name: string;
  url: string;
  width: number;
  height: number;
  glyphHeight: number;
  bottomPadding: number;
}

export const MAP_SPRITES = {
  /** 20 px disc, centred on the stop. */
  stop: {
    name: 'stop',
    url: new URL('@/assets/img/map/stop.svg', import.meta.url).href,
    width: 28,
    height: 28,
    glyphHeight: 20,
    bottomPadding: 4,
  },
  /** 27.6 × 34 px green pin, tip on the stop. */
  stopSelected: {
    name: 'stop_selected',
    url: new URL('@/assets/img/map/stop-selected.svg', import.meta.url).href,
    width: 27.6,
    height: 34,
    glyphHeight: 34,
    bottomPadding: 0,
  },
  /** 29.6 × 36 px pin, shadow included (32 px glyph), tip on the stop — first and last stop of a trip. */
  terminus: {
    name: 'terminus',
    url: new URL('@/assets/img/map/terminus.svg', import.meta.url).href,
    width: 29.6,
    height: 36,
    glyphHeight: 32,
    bottomPadding: 2,
  },
  /** 20 px geolocation disc, centred on the position. */
  userPosition: {
    name: 'user_position',
    url: new URL('@/assets/img/map/user-position.svg', import.meta.url).href,
    width: 28,
    height: 28,
    glyphHeight: 20,
    bottomPadding: 4,
  },
} satisfies Record<string, MapSprite>;

/**
 * The parts of a vehicle drawn under its badge, centred on the vehicle: the
 * heading arrow, pivoting on the image centre, and the badge shadow, both on a
 * 56 px canvas around the 32 px pill. The badge itself is drawn per route colour
 * by `addVehicleBadgeSprites`.
 */
export const VEHICLE_SPRITES = {
  arrow: {
    name: 'vehicle_arrow',
    url: new URL('@/assets/img/map/vehicle-arrow.svg', import.meta.url).href,
    width: 56,
    height: 56,
    glyphHeight: 32,
    bottomPadding: 12,
  },
  shadow: {
    name: 'vehicle_shadow',
    url: new URL('@/assets/img/map/vehicle-shadow.svg', import.meta.url).href,
    width: 56,
    height: 56,
    glyphHeight: 32,
    bottomPadding: 12,
  },
} satisfies Record<string, MapSprite>;

/** Bus pictogram of the badge, in the 32 px pill frame. */
export const VEHICLE_BUS_URL = new URL('@/assets/img/map/vehicle-bus.svg', import.meta.url).href;

/** The pill grows from 32 to 36 px once its vehicle is selected. */
export const VEHICLE_SELECTED_SCALE = 36 / 32;

/**
 * The same parts drawn at the selected size. Scaling the regular sprites with
 * `icon-size` would make MapLibre resample every badge, selected or not, and blur it:
 * each size gets its own raster, shown pixel for pixel.
 */
export const VEHICLE_SELECTED_SPRITES = {
  arrow: selectedVehicleSprite(VEHICLE_SPRITES.arrow),
  shadow: selectedVehicleSprite(VEHICLE_SPRITES.shadow),
} satisfies Record<keyof typeof VEHICLE_SPRITES, MapSprite>;

function selectedVehicleSprite(sprite: MapSprite): MapSprite {
  return {
    ...sprite,
    name: `${sprite.name}_selected`,
    width: sprite.width * VEHICLE_SELECTED_SCALE,
    height: sprite.height * VEHICLE_SELECTED_SCALE,
    glyphHeight: sprite.glyphHeight * VEHICLE_SELECTED_SCALE,
    bottomPadding: sprite.bottomPadding * VEHICLE_SELECTED_SCALE,
  };
}

export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load "${url}"`));
    image.src = url;
  });
}

/**
 * Rasterize an SVG at `pixelRatio` through a canvas. MapLibre's own `loadImage`
 * hands the decoded bitmap over untouched, so an SVG would be refused or drawn
 * at its nominal size and come out blurry on dense screens.
 */
export function rasterizeSvg(
  url: string,
  width: number,
  height: number,
  pixelRatio: number,
): Promise<ImageData> {
  return loadImage(url).then(image => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    const context = canvas.getContext('2d');
    if (!context) throw new Error(`Canvas 2D context unavailable, cannot rasterize "${url}"`);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return context.getImageData(0, 0, canvas.width, canvas.height);
  });
}

/**
 * Register `sprite` on the map, rendered for the current screen density. Must
 * be awaited before adding any layer using it as `icon-image`, otherwise the
 * layer can render first (MapLibre logs "Image ... could not be loaded").
 */
export async function addSvgSprite(
  map: MapLibreMap,
  sprite: MapSprite,
  pixelRatio: number = window.devicePixelRatio || 1,
): Promise<void> {
  const image = await rasterizeSvg(sprite.url, sprite.width, sprite.height, pixelRatio);
  map.addImage(sprite.name, image, { pixelRatio });
}

/** The badge, in the 32 px pill frame: white disc, route colour band across its bottom, border. */
const BADGE = {
  size: 32,
  radius: 15.5,
  borderColor: '#cccfcc',
  bandHeight: 16 / 3,
  bandRadius: 15,
};
/** Shown when a route colour cannot be drawn, like a route without one. */
const ROUTE_COLOR_FALLBACK = '#f0f0f0';

export function vehicleBadgeSpriteName(color: string, selected: boolean): string {
  return `vehicle_badge_${color}${selected ? '_selected' : ''}`;
}

/**
 * Register the badge of a vehicle of route colour `color`, at both sizes, unless
 * it is already there. One flat image rather than stacked layers, so a badge over
 * another hides it whole instead of letting the lower one's parts show through.
 * Drawn on a canvas: synchronous, so the images exist before the vehicles using
 * them reach the source, and anti-aliased. Cropped to the pill, since MapLibre
 * hit-tests the whole image and this is the layer that takes the tap.
 */
export function addVehicleBadgeSprites(
  map: MapLibreMap,
  color: string,
  bus: HTMLImageElement | undefined,
  pixelRatio: number = window.devicePixelRatio || 1,
): void {
  [false, true].forEach(selected => {
    const name = vehicleBadgeSpriteName(color, selected);
    if (map.hasImage(name)) return;
    const image = drawVehicleBadge(color, bus, (selected ? VEHICLE_SELECTED_SCALE : 1) * pixelRatio);
    if (image) map.addImage(name, image, { pixelRatio });
  });
}

function drawVehicleBadge(
  color: string,
  bus: HTMLImageElement | undefined,
  scale: number,
): ImageData | undefined {
  const pixels = Math.round(BADGE.size * scale);
  const canvas = document.createElement('canvas');
  canvas.width = pixels;
  canvas.height = pixels;
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  const { size, radius, borderColor, bandHeight, bandRadius } = BADGE;
  const centre = size / 2;
  context.scale(pixels / size, pixels / size);

  context.beginPath();
  context.arc(centre, centre, radius, 0, 2 * Math.PI);
  context.fillStyle = '#ffffff';
  context.fill();

  context.save();
  context.beginPath();
  context.arc(centre, centre, bandRadius, 0, 2 * Math.PI);
  context.clip();
  // An invalid colour string is ignored by the canvas, leaving the fallback.
  context.fillStyle = ROUTE_COLOR_FALLBACK;
  context.fillStyle = color;
  context.fillRect(0, centre + bandRadius - bandHeight, size, bandHeight);
  context.restore();

  if (bus) context.drawImage(bus, 0, 0, size, size);

  context.beginPath();
  context.arc(centre, centre, radius, 0, 2 * Math.PI);
  context.strokeStyle = borderColor;
  context.lineWidth = 1;
  context.stroke();

  return context.getImageData(0, 0, pixels, pixels);
}
